/**
 * salesService.ts
 * All API calls for the Sales Performance dashboard.
 * Security: calls go through /api/* (FastAPI backend) — Supabase Service Role Key
 * is NEVER exposed to the browser. Frontend only holds the backend URL.
 */
import axios from 'axios';

const api = axios.create({ baseURL: '/api', timeout: 15000 });

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LeadsDayData {
  date: string;   // YYYY-MM-DD
  count: number;
}

export interface LeadsPerDayResponse {
  data: LeadsDayData[];
  total: number;
}

export interface ProductBreakdown {
  count: number;
  total: number;
}

export interface SalesSummary {
  year_month: string;
  total_vendas: number;
  vendas_count: number;
  pending_count: number;
  cancelled_count: number;
  by_product: Record<string, ProductBreakdown>;
}

export interface SalesMeta {
  meta_mensal: number;
  updated_at: string | null;
}

// ─── Funil de Vendas ─────────────────────────────────────────────────────────

export interface FunnelMetrics {
  period: { start: string; end: string };
  fase1_diagnostico: number;
  fase2_audio:       number;
  fase3_venda:       number;
  fase4_compra:      number;
  conv_1_to_2: number;   // % leads → script
  conv_2_to_3: number;   // % script → venda
  conv_3_to_4: number;   // % venda → compra
  conv_total:  number;   // % leads → compra
}

/**
 * Extended state shape — structured for future injection of external metrics
 * (Instagram followers, TikTok views, etc.) without refactoring core logic.
 */
export interface PerformanceState {
  salesSummary: SalesSummary | null;
  meta: SalesMeta | null;
  leadsPerDay: LeadsDayData[];
  dateRange: { start: string; end: string };
  yearMonth: string;
  // Future: social / external metrics slot
  externalMetrics?: {
    instagram?: { followers: number; reach: number; reels_plays: number };
    tiktok?:    { followers: number; views: number };
    youtube?:   { subscribers: number; views: number };
    [key: string]: Record<string, number> | undefined;
  };
}

// ─── Manual Sale Input ──────────────────────────────────────────────────────

export interface VendaManualInput {
  produto: string;
  valor_bruto: number;
  status: 'pago' | 'pendente' | 'cancelado';
  data_hora?: string;  // ISO datetime string
  cliente?: string;
}

// ─── API Layer ───────────────────────────────────────────────────────────────

export const SalesService = {
  /**
   * Leads grouped by day in [startDate, endDate].
   * Protected against injection: uses Supabase SDK's parameterized queries on backend.
   */
  async getLeadsPerDay(startDate: string, endDate: string): Promise<LeadsPerDayResponse> {
    const { data } = await api.get<LeadsPerDayResponse>('/sales/leads-per-day', {
      params: { start_date: startDate, end_date: endDate },
    });
    return data;
  },

  /**
   * Monthly sales summary — only "pago" status is included in total.
   */
  async getSalesSummary(yearMonth: string): Promise<SalesSummary> {
    const { data } = await api.get<SalesSummary>('/sales/summary', {
      params: { year_month: yearMonth },
    });
    return data;
  },

  /** Get current monthly sales goal. */
  async getMeta(): Promise<SalesMeta> {
    const { data } = await api.get<SalesMeta>('/sales/meta');
    return data;
  },

  /** Persist updated monthly goal. */
  async setMeta(metaMensal: number): Promise<SalesMeta> {
    const { data } = await api.put<SalesMeta>('/sales/meta', { meta_mensal: metaMensal });
    return data;
  },

  /** Register a sale manually. */
  async addVendaManual(payload: VendaManualInput): Promise<{ status: string; data: any }> {
    const { data } = await api.post('/sales/venda', payload);
    return data;
  },

  /** Delete a sale by ID. */
  async deleteVenda(id: number): Promise<void> {
    await api.delete(`/sales/venda/${id}`);
  },

  /** Funil de vendas — 4 fases + taxas de conversão. */
  async getFunnelMetrics(startDate: string, endDate: string): Promise<FunnelMetrics> {
    const { data } = await api.get<FunnelMetrics>('/sales/funnel-metrics', {
      params: { start_date: startDate, end_date: endDate },
    });
    return data;
  },
};

// ─── Utility helpers ─────────────────────────────────────────────────────────

/** Format BRL currency */
export const formatBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** Returns color class based on percentage of goal reached */
export const getThermometerColor = (pct: number): string => {
  if (pct >= 100) return '#a855f7';  // purple — meta atingida!
  if (pct >= 67)  return '#22c55e';  // green  — no caminho
  if (pct >= 34)  return '#f59e0b';  // yellow — atenção
  return '#ef4444';                  // red    — crítico
};

/** ISO string for today / first-day-of-month in YYYY-MM-DD */
export const today = (): string => new Date().toISOString().slice(0, 10);
export const firstDayOfMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
export const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
